import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/api/profile'
import { usePortRequest, usePortRequests, type PortRequest } from '@/api/portage'
import { PortRequestDetailView } from '@/components/manager/numbers/portage/PortRequestDetailView'
import { PortWizardPage } from '@/components/manager/numbers/portage/PortWizardDialog'
import { useDeviceWidth } from '@/hooks/useDeviceWidth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { isPhoneOrTablet } from '@/lib/device'
import { useUIStore } from '@/stores/useUIStore'

interface WizardState {
  open: boolean
  transfer: PortRequest | null
  /** Opened from the transfer's page (correct, sign again): closing goes back to it. */
  fromDetail: boolean
}

/**
 * Transferring a number from a phone: a page of its own, outside the dashboard
 * (no menu), at the real phone width. /{lang}/transfert starts a request,
 * /{lang}/transfert/{id} follows or corrects one; the emails link here.
 * On a computer it hands over to the Numbers screen, where the wizard is a window.
 */
export function TransferPage() {
  const { t } = useTranslation()
  const { locale = 'fr', transferId } = useParams<{ locale: string; transferId?: string }>()
  const navigate = useNavigate()
  const [phone] = useState(isPhoneOrTablet)
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile, isLoading: profileLoading } = useProfile()
  const companyId = profile?.company ?? undefined
  const { data: ports } = usePortRequests(phone ? companyId : undefined)
  const { data: transfer } = usePortRequest(phone ? companyId : undefined, transferId)
  const [wizard, setWizard] = useState<WizardState>({ open: !transferId, transfer: null, fromDetail: false })

  useDeviceWidth()
  useDocumentTitle(t('manager.numbers.port.title'))

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
  }, [darkMode])

  // A draft opens on the wizard, where it was left.
  useEffect(() => {
    if (transfer?.status === 'brouillon') setWizard({ open: true, transfer, fromDetail: false })
  }, [transfer])

  // Back to the dashboard by a real page load: main.tsx then lays it out as usual.
  const backToCallizy = () => window.location.assign(`/${locale}/manager?view=numbers`)

  if (!phone) {
    const query = transferId ? `&transfer=${transferId}` : ''
    return <Navigate to={`/${locale}/manager?view=numbers${query}`} replace />
  }
  if (profile?.role === 'AGENT') {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }
  if (profileLoading || !companyId || (wizard.open && !ports)) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (wizard.open) {
    return (
      <PortWizardPage
        companyId={companyId}
        transfer={wizard.transfer}
        options={ports?.options}
        ownerEmail={profile?.email ?? ''}
        closeLabel={wizard.fromDetail ? t('manager.numbers.port.close') : t('manager.numbers.port.page.backToCallizy')}
        onClose={() => (wizard.fromDetail ? setWizard({ open: false, transfer: null, fromDetail: false }) : backToCallizy())}
        onSubmitted={(id) => {
          setWizard({ open: false, transfer: null, fromDetail: false })
          navigate(`/${locale}/transfert/${id}`, { replace: true })
        }}
      />
    )
  }

  return (
    <div className="min-h-[100dvh] bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 px-4 py-5">
      {transferId && (
        <PortRequestDetailView
          companyId={companyId}
          transferId={transferId}
          backLabel={t('manager.numbers.port.page.backToCallizy')}
          onBack={backToCallizy}
          onResume={(tr) => setWizard({ open: true, transfer: tr, fromDetail: true })}
        />
      )}
    </div>
  )
}
